import { Component, Input, OnInit } from '@angular/core';
import { IDropdown } from '@app/shared/models/dropdown.interface';
import { User } from '@app/shared/models/user.interface';
import { SessionService } from '@app/shared/services/session.service';
import { UserService } from '@app/shared/services/user/user.service';
import {
  GLOBAL_ADMIN_ROUTE,
  MANAGING_COMPANY_ADMIN_ROUTE,
  MANAGING_COMPANY_AUTHORITY_REQUESTS_ROUTE,
  USER_ROUTE
} from '@common/route-constants';
import { BehaviorSubject, combineLatest, concatMap, Observable, of } from 'rxjs';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { IsAuthorizedDirective } from '../../../directives/is-authorized.directive';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { MatTooltip } from '@angular/material/tooltip';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { IsFeatureAvailableDirective } from '@app/shared/directives/is-feature-available.directive';
import { IsAuthorizedForManagingCompanyDirective } from '@app/shared/directives/is-authorized-for-managing-company.directive';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';

@Component({
  selector: 'app-session-menu',
  templateUrl: './session-menu.component.html',
  styleUrls: ['../dropdown/dropdown.scss', './session-menu.component.scss'],
  imports: [
    NgClass,
    MatMenuTrigger,
    NgStyle,
    MatMenu,
    IsAuthorizedDirective,
    MatMenuItem,
    MatIcon,
    MatTooltip,
    AsyncPipe,
    RouterLink,
    IsFeatureAvailableDirective,
    IsAuthorizedForManagingCompanyDirective
  ]
})
export class SessionMenuComponent implements IDropdown, OnInit {
  constructor(public sessionService: SessionService, public userService: UserService, private route: ActivatedRoute) {}
  @Input() public user$: Observable<User>;

  public readonly isActive$ = new BehaviorSubject<boolean>(false);
  public readonly userInfoBaseUrl = USER_ROUTE.path;
  public readonly globalAdministrationBaseUrl = GLOBAL_ADMIN_ROUTE.path;

  protected readonly GlobalAuthority = GlobalAuthority;

  ngOnInit(): void {}

  logout(): void {
    this.sessionService.logout();
  }

  producePictureUrlStyle(user: User): { [key: string]: string } {
    return !!user && user.pictureUrl ? { 'background-image': `url(${user.pictureUrl})` } : {};
  }

  produceUserClass(user: User): { [key: string]: boolean } {
    const classes = {};
    if (!!user && !user.pictureUrl) {
      classes['or-icon-userpic'] = true;
      if (!this.isActive) {
        classes['or-inverse'] = true;
      }
    }

    return classes;
  }

  toggle(): void {
    this.isActive$.next(!this.isActive$.getValue());
  }

  isActive(): Observable<boolean> {
    return this.isActive$;
  }

  collapse(): void {
    this.isActive$.next(false);
  }

  collapseWithApply(): void {
    this.collapse();
  }

  getDropdownName(): string {
    return 'sessions';
  }

  setActive(state: boolean = true): void {
    this.isActive$.next(state);
  }

  getIsTenant(): Observable<boolean> {
    const currentBuilding$ = this.route.params.pipe(
      concatMap((params) => {
        const { buildingId } = params;
        return buildingId ? this.userService.getBuilding(buildingId) : of(null);
      }),
      distinctUntilChanged(),
      shareReplay(1)
    );

    const currentUser$ = this.userService.getCurrentUser().pipe(distinctUntilChanged(), shareReplay(1));

    return combineLatest([currentBuilding$, currentUser$]).pipe(
      map(([building, user]) => {
        if (building != null) {
          return user.tenantsMap[building.id]?.length > 0;
        } else {
          return false;
        }
      })
    );
  }

  getManagingCompanyAdminUrl(user: User | null | undefined): string | null {
    if (!user || !Array.isArray(user.managingCompanyAuthorities) || user.managingCompanyAuthorities.length === 0) {
      return null;
    }

    const firstAccessibleManagingCompany = user.managingCompanyAuthorities.find((mca) => {
      const authorities = mca.authorities ?? [];
      return (
        authorities.includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN) ||
        authorities.includes(ManagingCompanyAuthorityType.MANAGE_GLOBAL_AUTHORITY_REQUEST)
      );
    });

    if (!firstAccessibleManagingCompany) {
      return null;
    }

    const managingCompanyId = firstAccessibleManagingCompany.managingCompanyId;
    const authorities = firstAccessibleManagingCompany.authorities ?? [];

    if (authorities.includes(ManagingCompanyAuthorityType.MANAGING_COMPANY_ADMIN)) {
      return MANAGING_COMPANY_ADMIN_ROUTE.urlGenerator?.(managingCompanyId) ?? null;
    }

    if (authorities.includes(ManagingCompanyAuthorityType.MANAGE_GLOBAL_AUTHORITY_REQUEST)) {
      return MANAGING_COMPANY_AUTHORITY_REQUESTS_ROUTE.urlGenerator?.(managingCompanyId) ?? null;
    }

    return null;
  }

  protected readonly ManagingCompanyAuthorityType = ManagingCompanyAuthorityType;
}
